import { useEffect, useState } from "react"
import { LuFileCheck, LuMail, LuMessageCircle, LuMoon, LuPlus, LuSearch, LuSun } from "react-icons/lu"
import { FaSyncAlt } from "react-icons/fa"
import { Box, HStack, IconButton, Progress, Spacer, VStack } from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import { useAuth, useColorMode } from "@/contexts"
import { triggerNdaListener } from "@/api"
import { useMicrosoftConnection } from "@/hooks/emails/useMicrosoftConnection"
import { useSyncAll } from "@/hooks"
import { PortalRole } from "@/types"
import { CreateDealModal, SearchCompaniesAndDealsModal } from "../modals"
import { Button, Text, toaster } from "../ui"
import { ChatPanel } from "@/features/esoPerson/components/ChatPanel"
import { NotificationBell } from "@/features/esoPerson/components/NotificationBell"

export const DealManagerNavBar = () => {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
  const [isCreateDealModalOpen, setIsCreateDealModalOpen] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const { colorMode, toggleColorMode } = useColorMode()
  const { user } = useAuth()
  const isSean = user?.userType === PortalRole.ESO_PERSON && user.email === "sean@esofund.com"

  // Detect OAuth return (?microsoftAuth=success/error) and show toast
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const authResult = params.get("microsoftAuth")
    if (!authResult) return
    params.delete("microsoftAuth")
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname
    window.history.replaceState({}, "", newUrl)
    if (authResult === "success") {
      toaster.success({ title: "Microsoft account connected", description: "You can now send emails" })
    } else {
      toaster.error({ title: "Microsoft connection failed", description: "Please try again" })
    }
  }, [])

  return (
    <HStack pt={2} pb={2} pr={5} bg="nav.bg" w="100%" borderBottom="1px solid" borderColor="border.default">
      <Box
        ml={5}
        w="300px"
        border="1px solid"
        borderColor="border.default"
        borderRadius="full"
        p={2}
        cursor="text"
        onClick={() => setIsSearchModalOpen(true)}
      >
        <HStack>
          <LuSearch size={20} />
          <Text fontSize="sm" color="text.secondary">
            Search Companies or Deals...
          </Text>
        </HStack>
      </Box>
      <Spacer />
      {isSean && <ResyncButton />}
      {isSean && <NdaTriggerButton />}
      <MicrosoftReauthButton />
      <Button
        size="sm"
        variant="outline"
        onClick={() => setIsCreateDealModalOpen(true)}
      >
        <LuPlus />
        Create New Deal
      </Button>
      <NotificationBell />
      <IconButton
        aria-label="Open ESO Assistant"
        variant="ghost"
        size="sm"
        onClick={() => setIsChatOpen(true)}
        title="ESO Assistant"
      >
        <LuMessageCircle />
      </IconButton>
      <IconButton
        aria-label="Toggle color mode"
        variant="ghost"
        size="sm"
        onClick={toggleColorMode}
      >
        {colorMode === "light" ? <LuMoon /> : <LuSun />}
      </IconButton>
      <SearchCompaniesAndDealsModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />
      <CreateDealModal
        isOpen={isCreateDealModalOpen}
        onClose={() => setIsCreateDealModalOpen(false)}
      />
      <ChatPanel isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </HStack>
  )
}

const ResyncButton = () => {
  const { start, isPending, progress } = useSyncAll()

  const pct = progress
    ? Math.round((progress.completedCount / progress.totalCount) * 100)
    : 0

  return (
    <Box position="relative">
      <IconButton
        aria-label="Resync from legacy database"
        size="sm"
        variant="outline"
        loading={isPending && !progress}
        disabled={isPending}
        onClick={() => start()}
        title="Resync all data from legacy database"
      >
        <FaSyncAlt />
      </IconButton>

      {isPending && (
        <Box
          position="absolute"
          top="calc(100% + 6px)"
          right={0}
          w="220px"
          bg="bg.panel"
          border="1px solid"
          borderColor="border.default"
          borderRadius="md"
          p={3}
          zIndex={1000}
          shadow="md"
        >
          <VStack align="stretch" gap={1.5}>
            <Text fontSize="xs" fontWeight="semibold">
              {progress?.currentLabel ? `Syncing ${progress.currentLabel}…` : "Starting sync…"}
            </Text>
            <Progress.Root value={pct} size="xs">
              <Progress.Track borderRadius="full">
                <Progress.Range />
              </Progress.Track>
            </Progress.Root>
            <Text fontSize="xs" color="text.muted">
              {progress ? `${progress.completedCount} / ${progress.totalCount} steps` : "0 / 14 steps"}
            </Text>
          </VStack>
        </Box>
      )}
    </Box>
  )
}

const NdaTriggerButton = () => {
  const mutation = useMutation({
    mutationFn: () => triggerNdaListener(false),
    onSuccess: (data) => {
      const { summary } = data
      toaster.create({
        title: `NDA Listener: ${summary.uploaded} uploaded, ${summary.skipped} skipped, ${summary.errors} errors`,
        type: summary.errors > 0 ? "warning" : summary.uploaded > 0 ? "success" : "info",
      })
    },
    onError: () => {
      toaster.error({ title: "NDA listener failed — check console" })
    },
  })

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
    >
      <LuFileCheck />
      {mutation.isPending ? "Processing..." : "Process NDAs"}
    </Button>
  )
}

const MicrosoftReauthButton = () => {
  const { isConnected, isLoading, connect, isConnecting } = useMicrosoftConnection()

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => connect()}
      disabled={isConnecting || isLoading}
      colorPalette={isConnected ? "green" : "red"}
    >
      <LuMail />
      {isConnecting ? "Redirecting..." : isConnected ? "Email Connected" : "Connect Email"}
    </Button>
  )
}
